class UIController {
    constructor(context = {}) {
        this.button = null;
        this.nextAnchor = null;
        this.activeElement = null;
        this.context = context;
        
        // Use localized labels from WordPress if available
        const i18n = window.SaborlistaInfinite?.i18n || {};
        this.labels = {
            'post': i18n.next_article || 'Next Article',
            'recipe': i18n.next_recipe || 'Next Recipe',
            'news': i18n.next_news || 'Next News',
            'default': i18n.next_post || 'Next Post',
            'loading': i18n.loading || 'Loading...',
            'load_more': i18n.load_more || 'Load More'
        };
        
        this.init();
    }

    init() {
        // Create button
        this.button = document.createElement('div');
        this.button.className = 'infinite-next-post-btn is-hidden';
        this.button.innerHTML = `
            <span class="btn-text">${this.labels.default}</span>
            <span class="btn-loader" style="display: none;">${this.labels.loading}</span>
        `;
        
        this.button.addEventListener('click', () => this.scrollToNext());
        document.body.appendChild(this.button);

        // Listen to scroll to update visibility
        window.addEventListener('scroll', () => this.onScroll());
    }

    setLoading(isLoading) {
        const text = this.button.querySelector('.btn-text');
        const loader = this.button.querySelector('.btn-loader');
        
        if (isLoading) {
            this.button.classList.add('is-loading');
            if (text) text.style.opacity = '0.5';
            if (loader) loader.style.display = 'inline-block';
        } else {
            this.button.classList.remove('is-loading');
            if (text) text.style.opacity = '1';
            if (loader) loader.style.display = 'none';
        }
    }

    setActiveElement(element) {
        if (this.activeElement === element) return;
        
        this.activeElement = element;
        this.updateButtonText();
        this.onScroll();
    }

    setNextAnchor(element) {
        if (this.nextAnchor === element) return;
        this.nextAnchor = element;
        this.onScroll();

        if (this.pendingScroll) {
            this.pendingScroll = false;
            this.scrollToNext();
        }
    }

    updateButtonText() {
        if (!this.activeElement) return;

        let text;
        if (this.context && this.context.type !== 'single') {
            text = this.labels['load_more'];
        } else {
            // Look for data-type on the element itself or its first article child
            let type = this.activeElement.getAttribute('data-type');
            if (!type) {
                const article = this.activeElement.querySelector('article');
                if (article) type = article.getAttribute('data-type');
            }

            type = type || 'post';
            text = this.labels[type] || this.labels['default'];
        }

        const span = this.button.querySelector('.btn-text');
        if (span && span.textContent !== text) {
            span.textContent = text;
        }
    }

    scrollToNext() {
        if (!this.nextAnchor) {
            this.pendingScroll = true;
            this.setLoading(true);
            return;
        }

        // Try to find the title inside for more precise landing
        const target = this.nextAnchor.querySelector('.entry-title, .article-header, h1, h2') || this.nextAnchor;
        
        // Calculate dynamic offset from fixed headers
        let headerOffset = 0;
        const potentialHeaders = [
            '.service-menu', '.main-menu-wrapper', '.header', 
            '.secondary-menu-wrapper', '.mobile-header-bar'
        ];

        potentialHeaders.forEach(selector => {
            const el = document.querySelector(selector);
            if (el) {
                const style = window.getComputedStyle(el);
                if (style.position === 'fixed' || style.position === 'sticky') {
                    const rect = el.getBoundingClientRect();
                    // Only count if it's currently at the top
                    if (rect.top <= 1 && rect.bottom > 0) {
                        headerOffset += el.offsetHeight;
                    }
                }
            }
        });

        // Send analytics event
        if (window.SaborlistaAnalytics) {
            const nextUrl = this.nextAnchor.getAttribute('data-url');
            const nextTitle = this.nextAnchor.getAttribute('data-title');
            const currentUrl = this.activeElement ? this.activeElement.getAttribute('data-url') : window.location.href;

            window.SaborlistaAnalytics.sendEvent('infinite_next_click', {
                next_url: nextUrl,
                next_title: nextTitle,
                current_url: currentUrl
            });
        }

        // Add a bit of safety buffer
        const buffer = 20;
        const rect = target.getBoundingClientRect();
        const absoluteTop = rect.top + window.pageYOffset;
        const finalPosition = absoluteTop - headerOffset - buffer;

        window.scrollTo({
            top: finalPosition,
            behavior: 'smooth'
        });
    }

    onScroll() {
        if (!this.nextAnchor || !this.activeElement) {
            this.toggleButton(false);
            return;
        }

        const rect = this.activeElement.getBoundingClientRect();
        const postTop = rect.top + window.scrollY;
        const postHeight = rect.height;
        const currentScroll = window.scrollY;
        const viewportHeight = window.innerHeight;
        
        const scrollInsidePost = currentScroll - postTop;
        
        // Show after 40% of post or 1500px
        const reachedTrigger = scrollInsidePost > (postHeight * 0.4) || scrollInsidePost > 1500;

        // Hide if near the end of CURRENT post (500px)
        const distanceToEnd = (postTop + postHeight) - (currentScroll + viewportHeight);
        const nearEnd = distanceToEnd < 500;

        // Hide if NEXT post is already quite visible (more than 30% of viewport)
        const nextRect = this.nextAnchor.getBoundingClientRect();
        const nextInView = nextRect.top < (viewportHeight * 0.7);

        if (reachedTrigger && !nearEnd && !nextInView) {
            this.toggleButton(true);
        } else {
            this.toggleButton(false);
        }
    }

    toggleButton(show) {
        if (show) {
            if (this.button.classList.contains('is-hidden')) {
                this.button.classList.remove('is-hidden');
                this.button.classList.add('is-visible');
                // Dispatch event when button is shown
                document.dispatchEvent(new CustomEvent('saborlista:next-button-shown', { 
                    detail: { activeElement: this.activeElement } 
                }));
            }
        } else {
            if (this.button.classList.contains('is-visible')) {
                this.button.classList.remove('is-visible');
                this.button.classList.add('is-hidden');
            }
        }
    }
}

window.SaborlistaUIController = UIController;
